// Copyright © 2025 The Things Network Foundation, The Things Industries B.V.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from 'react'
import { useSelector } from 'react-redux'
import { defineMessages } from 'react-intl'

import lorawanStarterKit from '@assets/misc/starter-kit-visual-sidebar.png'

import Button from '@ttn-lw/components/button'
import Icon, { IconArrowRight } from '@ttn-lw/components/icon'

import Message from '@ttn-lw/lib/components/message'
import RequireRequest from '@ttn-lw/lib/components/require-request'

import {
  selectPluginTTSSandboxEnabled,
  selectPluginTTSStarterUser,
  selectPluginTTSStarterUserEnabled,
} from '@ttn-lw/lib/selectors/env'

import { getRegistryTotals } from '@console/store/actions/tenant.tti'

import { selectSubscriptionProduct } from '@console/store/selectors/subscription-management.tti'
import { selectConsolePreferences } from '@console/store/selectors/user-preferences'
import { selectRegistryTotals } from '@console/store/selectors/tenant.tti'

import style from './side-footer.styl'

const m = defineMessages({
  buttonMessage: 'Get your Starter Kit',
})

// TTI only.
const starterUserPlugin = selectPluginTTSStarterUser()
const isStarterUserPluginEnabled = selectPluginTTSStarterUserEnabled()
const isSandbox = selectPluginTTSSandboxEnabled()
// End TTI.

const StarterKitButton = () => {
  // TTI only.
  const subscriptionInfo = useSelector(selectSubscriptionProduct)
  const consolePreferences = useSelector(state => selectConsolePreferences(state))
  const tutorialsSeen = consolePreferences.tutorials?.seen || []
  const isBannerSeen = tutorialsSeen.includes('TUTORIAL_LORAWAN_STARTER_USER')
  const pluginDeviceThreshold = starterUserPlugin?.device_threshold
  const pluginGatewayThreshold = starterUserPlugin?.gateway_threshold
  const registryTotals = useSelector(selectRegistryTotals)
  const registeredDevices = registryTotals?.end_devices ?? 0
  const registeredGateways = registryTotals?.gateways ?? 0
  const isStarterUser = registryTotals
    ? pluginDeviceThreshold >= registeredDevices && pluginGatewayThreshold >= registeredGateways
    : isSandbox
  const starterKitUrl = starterUserPlugin?.starter_kit_url
  const isDiscovery = subscriptionInfo && subscriptionInfo.product.symbol === 'DISCOVERY'
  const showBanner =
    isStarterUserPluginEnabled && isBannerSeen && isStarterUser && (isDiscovery || isSandbox)
  // End TTI.

  if (!showBanner) {
    return null
  }

  return (
    <RequireRequest requestAction={getRegistryTotals()} handleErrors={false}>
      <Button.Link to={starterKitUrl} className={style.starterKitButton}>
        <div className="d-flex al-center gap-cs-m">
          <img src={lorawanStarterKit} alt="Starter Kit for LoRaWAN" />
          <Message content={m.buttonMessage} />
        </div>
        <Icon icon={IconArrowRight} />
      </Button.Link>
    </RequireRequest>
  )
}

export default StarterKitButton
